import additionalInfoPopupActionCreators from 'components/AdditionalInfoPopup/additionalInfoPopupActionCreators';
import { getCurrentTime } from 'components/InvitePopups/InviteUserPopup/constants';
import { getIsEmployeesLimitTOVReached } from 'selectors/app.selectors';
import { sendAnalyticsTrialEmployeeLimitReachedPopup } from 'services/billing';
import { EmployeeLimitReachedEventType } from 'services/enums';
import { t } from 'ttag';

import { LOCALES, changeLocale } from '../../lib/i18n/utils';

import { Thunk } from '../../types';
import { TriggerNotification } from '../../types/triggerNotifications';

import appActionCreators from '../app/appActionCreators';
import appActions from '../app/appActions';
import checkKeyPopupActionCreators from '../checkKeyPopup/checkKeyPopupActionCreators';
import filtersActionCreators from '../filters/filtersActionCreators';
import proRateInfoPopupActionCreators from '../proRateInfoPopup/proRateInfoPopupActionCreators';
import triggerNotificationActionCreators from '../triggerNotification/triggerNotificationActionCreators';
import actions from './headerActions';

import eventTracking from '../../services/analytics/eventTracking';
import auth from '../../services/auth';
import { getLocationId } from '../../services/navigation-structure';
import { changeLanguage, denyRole, updateRole } from '../../services/user';
import {
    getCurrentOnboardingTrackingCategory,
    resetOnboardingState,
} from '../Onboarding/onboardingReducer';

function hideInviteTooltip(): Thunk {
    return async (dispatch, getState) => {
        const {
            header: { isTooltipActive },
            app: {
                currentUser: { currentRole },
            },
        } = getState();
        if (currentRole.showInviteTooltip) {
            await updateRole(currentRole.id, { show_invite_tooltip: false });
            if (isTooltipActive)
                dispatch({ type: actions.HEADER__CLOSE_TOOLTIP });
        }
    };
}

function onShow(): Thunk {
    return async (dispatch, getState) => {
        const {
            app: { needToVerifyEmail, needToVerifyUser, currentUser },
        } = getState();
        dispatch({
            type: actions.HEADER__SET_INVITE_PERMISSION,
            isInviteAllowed: !needToVerifyEmail && !needToVerifyUser,
        });

        // Load trigger notifications
        if (!currentUser.registrationCompleted) {
            return;
        }

        dispatch(triggerNotificationActionCreators.load(25));
    };
}

function onOpenInvitePopup(
    isInviteEmployeeMode = false,
    fromOnboarding = false,
): Thunk {
    return (dispatch, getState) => {
        const {
            onboarding: { currentState },
            app: {
                flags: {
                    ['ACTIVATE_TRIAL_WHEN_EMPLOYEES_LIMIT_REACHED']: isActivateTrialFlagEnabled,
                },
            },
        } = getState();

        const showContentForTrialWhenEmployeesLimitTOVReached = getIsEmployeesLimitTOVReached(
            getState(),
        );

        if (isInviteEmployeeMode) {
            dispatch({ type: appActions.APP__SHOW_INVITE_USER_POPUP });

            //Send analytics for trial employee limit reached popup
            if (
                isActivateTrialFlagEnabled &&
                showContentForTrialWhenEmployeesLimitTOVReached
            ) {
                sendAnalyticsTrialEmployeeLimitReachedPopup(
                    EmployeeLimitReachedEventType.EMPLOYEE_LIMIT_REACHED_SHOWN,
                    getCurrentTime(),
                );
            }
            if (
                !isActivateTrialFlagEnabled &&
                showContentForTrialWhenEmployeesLimitTOVReached
            ) {
                sendAnalyticsTrialEmployeeLimitReachedPopup(
                    EmployeeLimitReachedEventType.EMPLOYEE_LIMIT_REACHED_NOT_SHOWN,
                    getCurrentTime(),
                );
            }
        }
        if (!isInviteEmployeeMode) {
            dispatch({
                type: appActions.APP__SHOW_INVITE_RECIPIENT_POPUP,
            });
        }
        dispatch({ type: actions.HEADER__CLOSE_TOOLTIP });

        if (currentState === 'INVITE_BANNER') {
            eventTracking.sendEvent(
                getCurrentOnboardingTrackingCategory(getState()),
                'invite-colleague',
                fromOnboarding ? 'from-onboarding' : 'main-btn',
            );
        }
    };
}

function onCloseTooltip(): Thunk {
    return async (dispatch) => {
        dispatch({ type: actions.HEADER__CLOSE_TOOLTIP });
        dispatch(hideInviteTooltip);
    };
}

function onAddCompany(isAddCompanyMode: boolean, edrpou: string): Thunk {
    return (dispatch) => {
        dispatch(
            checkKeyPopupActionCreators.onShow(
                'add_company',
                isAddCompanyMode,
                edrpou,
            ),
        );
    };
}

function onChangeRole(roleId: string, forceUpdateRoute: string): Thunk {
    return async (dispatch) => {
        try {
            await auth.activateRole(roleId);
            await dispatch(
                appActionCreators.onRefreshCurrentUser(true, forceUpdateRoute),
            );
            await dispatch(appActionCreators.reloadFeatureFlags());
            dispatch(resetOnboardingState());
            await dispatch(filtersActionCreators.onReloadTags());
            eventTracking.sendEvent(
                'company_select',
                'change_company',
                getLocationId(document.location),
            );
        } catch (err) {
            dispatch(
                appActionCreators.onAlertPopupShow(t`Помилка!`, err.message),
            );
        }
    };
}

function onChangeLocale(locale: LOCALES): Thunk {
    return async (dispatch) => {
        try {
            await changeLanguage(locale);

            changeLocale(locale);
        } catch (err) {
            dispatch(
                appActionCreators.onAlertPopupShow(t`Помилка!`, err.message),
            );
        }
    };
}

function onRefreshCurrentUser(roleId: string): Thunk {
    return async (dispatch) => {
        try {
            await denyRole(roleId);
            await dispatch(appActionCreators.onRefreshCurrentUser());
            eventTracking.sendEvent('company_select', 'hide_pending_company');
        } catch (err) {
            dispatch(
                appActionCreators.onAlertPopupShow(t`Помилка!`, err.message),
            );
        }
    };
}

function onOpenAddInfoPopup(): Thunk {
    return (dispatch) => {
        dispatch(additionalInfoPopupActionCreators.setDisplayStatusVisible());
    };
}

function onOpenContactsInvitePopup(): Thunk {
    return (dispatch) => {
        dispatch({ type: appActions.APP__SHOW_INVITE_RECIPIENTS_POPUP });
    };
}

function onOpenProRatePopup(): Thunk {
    return (dispatch) => {
        dispatch(
            proRateInfoPopupActionCreators.showProRatePopupToCompany(
                'banner-header-turn-on-trial',
            ),
        );
    };
}

function onShowTriggerNotifications(
    newTriggerNotifications: TriggerNotification[],
): Thunk {
    return async (dispatch, getState) => {
        const {
            header: { hasSeenTriggerNotifications },
        } = getState();

        // First open trigger notifications
        if (!hasSeenTriggerNotifications) {
            dispatch({
                type: actions.HEADER__SEEN_TRIGGER_NOTIFICATIONS,
                hasSeenTriggerNotifications: true,
            });

            dispatch(
                triggerNotificationActionCreators.updateNotifications(
                    newTriggerNotifications,
                ),
            );
        }

        // Analytics
        const label = hasSeenTriggerNotifications
            ? 'following_open'
            : 'first_open';
        eventTracking.sendEvent('notification_bell', 'show', label);
    };
}

function onClickTriggerNotification(notification: TriggerNotification): Thunk {
    return (dispatch) => {
        dispatch(triggerNotificationActionCreators.onClick(notification));

        // Analytics
        eventTracking.sendEvent(
            'notification_bell',
            'click',
            notification.title,
        );
    };
}

export default {
    hideInviteTooltip,
    onAddCompany,
    onChangeRole,
    onChangeLocale,
    onCloseTooltip,
    onOpenAddInfoPopup,
    onOpenContactsInvitePopup,
    onOpenInvitePopup,
    onOpenProRatePopup,
    onRefreshCurrentUser,
    onShow,

    onShowTriggerNotifications,
    onClickTriggerNotification,
};
